import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { useState } from 'react';
import {
    useAddHostReplyMutation,
    useUpdateHostReplyMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import { ReviewCard } from '../../Reviews';
import toast from '../../../shared/Toast';
import { UserReview } from '../../../types/review';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    bookingId: number;
    reservationDetails: any;
    reviews: UserReview[];
}

const HostReplyEditor: React.FC<{
    review: UserReview;
    onDone: () => void;
}> = ({ review, onDone }) => {
    const [replyText, setReplyText] = useState(review.hostReplyText || '');
    const [addHostReply, { isLoading: isCreating }] = useAddHostReplyMutation();
    const [updateHostReply, { isLoading: isUpdating }] =
        useUpdateHostReplyMutation();
    const hasReply = Boolean(review.hostReplyText);

    const handleSave = async () => {
        if (!replyText.trim()) return;
        try {
            if (hasReply) {
                await updateHostReply({
                    reviewId: review.id,
                    replyText: replyText.trim(),
                }).unwrap();
            } else {
                await addHostReply({
                    reviewId: review.id,
                    replyText: replyText.trim(),
                }).unwrap();
            }
            onDone();
            toast.success('Reply saved successfully');
        } catch {
            toast.error('Failed to save the reply. Please try again.');
        }
    };

    return (
        <Stack spacing={1}>
            <TextField
                label={hasReply ? 'Edit your reply' : 'Reply to this review'}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                size='small'
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                    variant='contained'
                    size='small'
                    onClick={handleSave}
                    disabled={!replyText.trim() || isCreating || isUpdating}
                >
                    {hasReply ? 'Update reply' : 'Post reply'}
                </Button>
            </Box>
        </Stack>
    );
};

const ReviewsModalForReservation: React.FC<ModalProps> = ({
    open,
    onClose,
    bookingId,
    reservationDetails,
    reviews,
}) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const loggedInUser = UserRole();
    const isOwner = loggedInUser?.role?.includes('owner');
    const [editingReplyId, setEditingReplyId] = useState<number | null>(null);
    const handleClose = () => {
        setEditingReplyId(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth='md'
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    maxHeight: '95vh',
                    overflow: 'hidden',
                },
            }}
        >
            <DialogTitle>
                Reviews for {reservationDetails?.module?.name}
                <IconButton onClick={handleClose} size='small'>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {!reviews && <LoadingComponent />}

                {reviews && Array.isArray(reviews) && reviews.length > 0 && (
                    <Stack spacing={3}>
                        {reviews.map((review) => (
                            <ReviewCard
                                key={review.id}
                                review={review}
                                showCategoryRatings={true}
                                isInModal={true}
                                hideHostReply={editingReplyId === review.id}
                                replyActions={
                                    isOwner && !loggedInUser?.viewOnly ? (
                                        editingReplyId === review.id ? (
                                            <HostReplyEditor
                                                review={review}
                                                onDone={() =>
                                                    setEditingReplyId(null)
                                                }
                                            />
                                        ) : (
                                            <Button
                                                size='small'
                                                variant='outlined'
                                                onClick={() =>
                                                    setEditingReplyId(review.id)
                                                }
                                            >
                                                {review.hostReplyText
                                                    ? 'Edit reply'
                                                    : 'Reply'}
                                            </Button>
                                        )
                                    ) : undefined
                                }
                            />
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewsModalForReservation;
